<template>
  <div class="config-field">
    <!-- Special inputs for specific config id's -->
    <daysBeforeDefault
      v-if="id === 'DaysBeforeDefault'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideTooltip="hideTooltip"
      :id="id"
      :parentField="parentField"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <denyReasonConfig
      v-else-if="id === 'UseBODenyReasonConfiguration'"
      :category="category"
      :id="id"
      :label="label"
      ref="configField"
      :rules="rules"
      :value="value"
      :parentField="parentField"
      :availableFieldList="availableFieldList"
      :ruleId="ruleId"
      @setConfigValue="setConfigValue"
    />
    <factors
      v-else-if="id === 'FirstPartyFraudFactors' || id === 'TrueFraudFactors'"
      :category="category"
      :id="id"
      :label="label"
      ref="configField"
      :rules="rules"
      :value="value"
      :parentField="parentField"
      :availableFieldList="availableFieldList"
      :ruleId="ruleId"
      @setConfigValue="setConfigValue"
    />

    <!-- Type-based inputs -->
    <buttonGroup
      v-else-if="type === 'buttonGroup'"
      :category="category"
      :helpContent="helpContent"
      :hideTooltip="hideTooltip"
      :id="id"
      :label="label"
      :options="options"
      :parentField="parentField"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      @setConfigValue="setConfigValue"
    />
    <checkbox
      v-else-if="type === 'checkbox'"
      :availableFieldList="availableFieldList"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideTooltip="hideTooltip"
      :id="id"
      :label="label"
      :parentField="parentField"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :warning="warning"
      @setConfigValue="setConfigValue"
    />
    <date
      v-else-if="type === 'date'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :id="id"
      :label="label"
      ref="configField"
      :rules="rules"
      :parentField="parentField"
      :tooltip="tooltip"
      :value="value"
      @setConfigValue="setConfigValue"
    />
    <dropdown
      v-else-if="type === 'dropdown'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :isInModal="isInModal"
      :label="label"
      :options="options"
      :parentField="parentField"
      :placeholder="placeholder"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :warning="warning"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <dropdown
      v-else-if="type === 'multiSelectDropdown'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :isInModal="isInModal"
      :label="label"
      :options="options"
      :parentField="parentField"
      :placeholder="placeholder"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :warning="warning"
      :width="width"
      @setConfigValue="setConfigValue"
      :multiple="true"
    />
    <dropdownTable
      v-else-if="type === 'dropdownTable'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :label="label"
      :options="options"
      :parentField="parentField"
      :placeholder="placeholder"
      ref="configField"
      :ruleId="ruleId"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :warning="warning"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <configTable
      v-else-if="type === 'configTable'"
      :category="category"
      :disabled="disabled"
      :headers="headers"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :label="label"
      :options="availableFieldList"
      :parentField="parentField"
      :placeholder="placeholder"
      ref="configField"
      :ruleId="ruleId"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :warning="warning"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <list
      v-else-if="type === 'list'"
      :category="category"
      :hideLabel="hideLabel"
      :id="id"
      :isTemplate="isTemplate"
      :label="label"
      ref="configField"
      :rules="rules"
      :value="value || []"
      :parentField="parentField"
      :availableFieldList="availableFieldList"
      :ruleId="ruleId"
      @setConfigValue="setConfigValue"
    />
    <number
      v-else-if="type === 'number'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :label="label"
      :parentField="parentField"
      :placeholder="placeholder"
      :prefix="prefix"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value === '' ? undefined : Number(value)"
      :warning="warning"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <objectField
      v-else-if="type === 'object'"
      :category="category"
      :hideLabel="hideLabel"
      :helpContent="helpContent"
      :id="id"
      :label="label"
      :layout="layout"
      ref="configField"
      :ruleId="ruleId"
      :parentField="parentField"
      @setConfigValue="setConfigValue"
      @unsetConfigValue="unsetConfigValue"
    />
    <paragraph
      v-else-if="type === 'paragraph'"
      :category="category"
      :hideLabel="hideLabel"
      :id="id"
      :label="label"
      ref="configField"
      :rules="rules"
      :value="value"
      :parentField="parentField"
      @setConfigValue="setConfigValue"
    />
    <phone
      v-else-if="type === 'phone'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :label="label"
      :parentField="parentField"
      :placeholder="placeholder"
      :prefix="prefix"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <postalCode
      v-else-if="type === 'postalCode'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :label="label"
      :parentField="parentField"
      :placeholder="placeholder"
      :prefix="prefix"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <radio
      v-else-if="type === 'radio'"
      :category="category"
      :deselectable="ruleId === 'bulkEdit'"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :id="id"
      :label="label"
      :options="options"
      :parentField="parentField"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      @setConfigValue="setConfigValue"
      @unsetConfigValue="unsetConfigValue"
    />
    <string
      v-else-if="type === 'string'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :label="label"
      :message="message"
      :parentField="parentField"
      :placeholder="placeholder"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <toggle
      v-else-if="type === 'toggle'"
      :category="category"
      :disabled="disabled"
      :helpContent="helpContent"
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :icon="icon"
      :id="id"
      :label="label"
      :message="message"
      :parentField="parentField"
      :placeholder="placeholder"
      ref="configField"
      :rules="rules"
      :tooltip="tooltip"
      :value="value"
      :width="width"
      @setConfigValue="setConfigValue"
    />
    <contentField
      v-else-if="type === 'content'"
      :category="category"
      :id="id"
      :content="field.Content"
      :helpContent="helpContent"
      :tooltip="tooltip"
    />
    <div v-else>
      {{ unsupportedTypeWarning(type) }}
    </div>
  </div>
</template>

<script>
/**
 * @fileoverview Wrapper for various config fields
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";

import daysBeforeDefault from "./daysBeforeDefault";
import denyReasonConfig from "./denyReasonConfig";
import buttonGroup from "./buttonGroup";
import checkbox from "./checkbox";
import configTable from "./configTable.vue";
import date from "./date";
import dropdown from "./dropdown";
import dropdownTable from "./dropdownTable";
import factors from "./factors.vue";
import list from "./list";
import number from "./number";
import objectField from "./object";
import paragraph from "./paragraph";
import phone from "./phone";
import postalCode from "./postalCode";
import radio from "./radio";
import string from "./string";
import toggle from "./toggle";
import contentField from "./content";

export default {
  name: "ConfigField",
  components: {
    daysBeforeDefault,
    denyReasonConfig,
    buttonGroup,
    checkbox,
    configTable,
    date,
    dropdown,
    dropdownTable,
    factors,
    list,
    number,
    objectField,
    paragraph,
    phone,
    postalCode,
    radio,
    string,
    toggle,
    contentField,
  },
  props: {
    category: {
      type: String,
    },
    icon: {
      type: String,
    },
    id: {
      type: String,
    },
    availableFieldList: {
      type: Array,
    },
    disabled: {
      type: Boolean,
    },
    field: {
      type: Object,
      required: true,
    },
    headers: {
      type: Array,
      default() {
        return [];
      }
    },
    hideLabel: {
      type: Boolean,
    },
    hideTooltip: {
      type: Boolean,
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    layout: {
      type: Array,
    },
    message: {
      type: String,
    },
    options: {
      type: Array,
    },
    parentField: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    prefix: {
      type: String,
    },
    rules: {
      type: Array,
    },
    ruleId: {
      type: String,
    },
    helpContent: {
      type: String,
    },
    tooltip: {
      type: String,
    },
    type: {
      type: String,
    },
    value: {
      type: [String, Object, Number, Boolean, Array],
    },
    warning: {
      type: String,
    },
    width: {
      type: String,
    },
  },
  methods: {
    /**
     * Sets the value
     */
    setConfigValue(value) {
      let fieldId;
      let configValue = value;

      if (this.parentField !== undefined) {
        fieldId = this.parentField;
        configValue = { [this.id]: value };
      } else {
        fieldId = this.id;
      }

      this.$emit("setConfigValue", {
        category: this.category,
        fieldId,
        value: configValue,
      });
    },
    unsetConfigValue(value) {
      const fieldId =
        (this.parentField !== undefined ? this.parentField + "." : "") +
        this.id;
      this.$emit("unsetConfigValue", {
        category: this.category,
        fieldId,
        value,
      });
    },
    unsupportedTypeWarning(type) {
      if (this.debugMode) {
        console.warn("The field type \x1B[1m%s\x1B[m is not supported.", type);
      }
    },
    validate() {
      if (this.$refs.configField && this.$refs.configField.validate) {
        return this.$refs.configField.validate();
      }
      return true;
    },
  },
  computed: {
    ...mapGetters({
      debugMode: "getDebugMode",
    }),
  },
};
</script>

<style lang="scss" scoped>
.no-margin .config-field {
  margin-bottom: 0 !important;
}
</style>